import type { AxiosRequestConfig } from 'axios';
import { isAxiosError } from 'axios';
import z from 'zod';
import { api } from '../../axios.instance';
import { responseContract } from '../../lib';
import {
  type GetComparisonTableRequestDto,
  getComparisonTable,
} from './get-comparison-table';

export const GetAlertsTrendsTableRequestDtoSchema = z.object({
  facilityId: z.number(),
  from: z.string(),
  to: z.string(),
  comparisonSet: z.enum(['INTERNAL', 'COMP']).optional(),
});

const MetricValueSchema = z.object({
  owned: z.number().nullable(),
  compSet: z.number().nullable(),
  deltaVsMarket: z.number().nullable(),
});

export const GetAlertsTrendsTableResponseDtoSchema = z.object({
  periods: z.array(
    z.object({
      period: z.string(),
      dateRange: z.string(),
      alertsPerPerson: MetricValueSchema,
      flagsPerPerson: MetricValueSchema,
      avgDaysToResolve: MetricValueSchema,
      alertsResolutionEffectiveness: MetricValueSchema,
    })
  ),
});

export type GetAlertsTrendsTableRequestDto = z.infer<
  typeof GetAlertsTrendsTableRequestDtoSchema
>;

export type GetAlertsTrendsTableResponseDto = z.infer<
  typeof GetAlertsTrendsTableResponseDtoSchema
>;

export const getAlertsTrendsTable = async (
  params: GetAlertsTrendsTableRequestDto,
  config?: AxiosRequestConfig<GetAlertsTrendsTableRequestDto>
) => {
  const { facilityId, ...restParams } = params;

  try {
    return await api
      .get(`market-intelligence/facilities/${facilityId}/trends-table-alerts`, {
        params: restParams,
        ...config,
      })
      .then(responseContract(GetAlertsTrendsTableResponseDtoSchema));
  } catch (error) {
    if (!isAxiosError(error) || error.response?.status !== 404) {
      throw error;
    }

    const comparisonTable = await getComparisonTable(
      params as GetComparisonTableRequestDto,
      config as AxiosRequestConfig<GetComparisonTableRequestDto>
    );

    return {
      periods: comparisonTable.periods.map((period) => ({
        period: period.period,
        dateRange: period.dateRange,
        alertsPerPerson: period.alertsPerPerson,
        flagsPerPerson: period.flagsPerPerson,
        avgDaysToResolve: period.avgDaysToResolve,
        alertsResolutionEffectiveness: period.alertsResolutionEffectiveness,
      })),
    };
  }
};
